import type { ComboboxItem } from "@mantine/core";

import {
  type IUserForm,
  type LogbookSelectItem,
  type PermSelectItem,
} from "./UserForm.types";

const mapLogbookItem = (
  log: any,
  visited = new Set<string>(),
): LogbookSelectItem | null => {
  if (!log?.lbname || !log?.lbid || visited.has(log.lbid)) {
    return null;
  }

  const nextVisited = new Set(visited);
  nextVisited.add(log.lbid);

  const parts = Array.isArray(log.parts)
    ? log.parts
        .map((part: any) => mapLogbookItem(part, nextVisited))
        .filter((part): part is LogbookSelectItem => !!part)
    : [];

  return {
    value: log.lbid,
    label: log.lbname,
    ...(parts.length > 0 ? { parts } : {}),
  };
};

export const mapUserForm = (data: any) => {
  const initialValues = {
    type: (data?.data?.user?.usertype || "") as string,
    role: (data?.data?.user?.roletype || "") as string,
    fname: (data?.data?.user?.firstname || "") as string,
    sname: (data?.data?.user?.surname || "") as string,
    phoneno: (data?.data?.user?.phoneno || "") as string,
    mobile: (data?.data?.user?.mobile || "") as string,
    email: (data?.data?.user?.email || "") as string,
    personalEmail: (data?.data?.user?.personalEmail || "") as string,
    active: (data?.data?.user?.active ?? true) as boolean,
    deleted: (data?.data?.user?.deleted ?? false) as boolean,
    medofficer: (data?.data?.user?.medofficer ?? false) as boolean,
    rotation: (data?.data?.user?.rotation ?? false) as boolean,
    logbooks: (data?.data?.user?.logbooks || []) as string[],
    perms: (data?.data?.user?.perms || []) as string[],
    extref: (data?.data?.user?.extref || "") as string,
    dischargeID: (data?.data?.user?.dischargeID || "") as string,
    seabodyID: (data?.data?.user?.seabodyID || "") as string,
    coc: (data?.data?.user?.coc || []) as any[],
    datejoined:
      data?.data?.user && "datejoined" in data.data.user
        ? (data.data.user.datejoined as string)
        : undefined,
  };
  const adminPerms: PermSelectItem[] = [];
  const userPerms: PermSelectItem[] = [];

  for (const perm of data.data.perms) {
    const temp = {
      value: String(perm.perm),
      label: perm.name,
      description: perm.description,
    };
    if (perm.cat === "Yacht Admin") {
      adminPerms.push(temp);
    } else if (perm.cat === "Yacht Users") {
      userPerms.push(temp);
    }
  }

  const logbooks: LogbookSelectItem[] = [];

  for (const log of data.data.logbooks) {
    const mappedLogbook = mapLogbookItem(log);
    if (mappedLogbook) {
      logbooks.push(mappedLogbook);
    }
  }

  return {
    selects: {
      types: data.data.types.map((type: { id: string; name: string }) => ({
        value: String(type.id),
        label: type.name,
      })),
      roles: data.data.roles.map(
        (role: { crid: string; rolename: string }) => ({
          value: String(role.crid),
          label: role.rolename,
        }),
      ),
      logbooks,
      adminPerms,
      userPerms,
    },
    initialValues,
  };
};

/** Copies settings from `source` onto the empty "new" payload; personal fields stay empty. */
export const buildCopiedUserValues = (
  emptyValues: IUserForm,
  source: IUserForm,
): IUserForm => ({
  ...emptyValues,
  type: source.type,
  role: source.role,
  logbooks: source.logbooks,
  perms: source.perms,
  medofficer: source.medofficer,
  rotation: source.rotation,
  active: source.active,
  deleted: false,
});

/** Defaults an empty `datejoined` (BE supports it) to `today` when creating a new user. */
export const withJoinDateDefault = (
  values: IUserForm,
  isCreate: boolean,
  today: string,
): IUserForm => {
  if (isCreate && values.datejoined === "") {
    return { ...values, datejoined: today };
  }
  return values;
};

export const mapUserSelects = (data: any) => {
  const adminPerms: PermSelectItem[] = [];
  const userPerms: PermSelectItem[] = [];

  for (const perm of data.data.perms) {
    const temp = {
      value: String(perm.perm),
      label: perm.name,
      description: perm.description,
    };
    if (perm.cat === "Yacht Admin") {
      adminPerms.push(temp);
    } else if (perm.cat === "Yacht Users") {
      userPerms.push(temp);
    }
  }
  const roles = data.data.roles.map(
    (role: { crid: string; rolename: string }) => ({
      value: String(role.crid),
      label: role.rolename,
    }),
  ) as ComboboxItem[];

  return { adminPerms, userPerms, roles };
};
